import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { SCHOOL, IMAGES, PILLARS, STATS } from '../data/siteData';
import { Section, SectionHeading, Reveal, ImageBox, Icon, CountUp, Wave, floatSlow, wiggle } from './common';

export default function About() {
  return (
    <Section id="about" bg="#fff" sx={{ pt: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <SectionHeading
          eyebrow="About Us"
          title="A Happy Second Home for Your Little One"
          subtitle={`${SCHOOL.fullName} brings you a caring Day Care where every child is loved, heard and encouraged to explore.`}
          color={COLORS.pink}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: { xs: 8, md: 8 }, alignItems: 'center' }}>
          <Reveal direction="left">
            <Box sx={{ position: 'relative', pb: { xs: 6, md: 8 }, pr: { xs: 4, md: 8 } }}>
              <Box sx={{ position: 'absolute', top: -20, left: -20, width: 120, height: 120, borderRadius: '50%', bgcolor: `${COLORS.yellow}66`, zIndex: 0 }} />
              <Box sx={{ position: 'relative', zIndex: 1, borderRadius: '40px 120px 40px 120px', overflow: 'hidden', border: `8px solid ${COLORS.pink}`, boxShadow: '0 20px 40px rgba(255,77,151,.3)' }}>
                <ImageBox src={IMAGES.about1} label="About image: classroom moments" color={COLORS.pink} ratio="4 / 4.2" radius={0} />
              </Box>
              <Box sx={{ position: 'absolute', right: 0, bottom: 0, width: '50%', zIndex: 2, borderRadius: '50px 20px 50px 20px', overflow: 'hidden', border: `8px solid ${COLORS.blue}`, boxShadow: '0 16px 30px rgba(45,156,255,.35)', animation: `${floatSlow} 8s ease-in-out infinite` }}>
                <ImageBox src={IMAGES.about2} label="Playtime" color={COLORS.blue} ratio="1 / 1" radius={0} />
              </Box>
              <Box sx={{ position: 'absolute', top: 24, right: { xs: 8, md: 40 }, zIndex: 3, bgcolor: COLORS.yellow, color: COLORS.navy, borderRadius: '50%', width: 110, height: 110, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', boxShadow: '0 10px 24px rgba(255,200,61,.6)', animation: `${wiggle} 5s ease-in-out infinite` }}>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 28, lineHeight: 1 }}>1.5-5</Typography>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 700, fontSize: 15, lineHeight: 1.1 }}>Years Welcome</Typography>
              </Box>
            </Box>
          </Reveal>

          <Box>
            <Reveal direction="right">
              <Typography variant="h3" sx={{ color: COLORS.navy, fontSize: { xs: 28, md: 36 }, lineHeight: 1.15 }}>
                Where learning feels like play
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 17, lineHeight: 1.8 }}>
                At St Lord's Day Care, we believe the early years are the most precious years. Our warm caregivers, thoughtfully planned
                activities and safe, colourful classrooms help children aged 1.5 to 5 years grow in body, mind and heart.
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 17, lineHeight: 1.8 }}>
                From the moment they arrive, children enjoy songs, stories, art, outdoor games and healthy meals, while parents enjoy
                peace of mind knowing their little one is happy and cared for.
              </Typography>
            </Reveal>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 4 }}>
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} direction="up" delay={i * 120}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                      p: 2,
                      borderRadius: '24px',
                      bgcolor: `${p.color}14`,
                      border: `2px dashed ${p.color}`,
                      height: '100%',
                      transition: 'transform .3s ease',
                      '&:hover': { transform: 'scale(1.04) rotate(-1deg)' },
                    }}
                  >
                    <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', bgcolor: p.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="check" />
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 20, color: COLORS.navy, lineHeight: 1.1 }}>{p.title}</Typography>
                      <Typography sx={{ fontSize: 14.5, lineHeight: 1.55, mt: 0.5 }}>{p.text}</Typography>
                    </Box>
                  </Box>
                </Reveal>
              ))}
            </Box>

            <Reveal delay={200} sx={{ mt: 4 }}>
              <Button
                href="#admission"
                variant="contained"
                endIcon={<Icon name="arrow" />}
                sx={{ px: 4, py: 1.4, fontSize: 17, bgcolor: COLORS.blue, boxShadow: '0 7px 0 #1C7ED6', '&:hover': { bgcolor: '#4aa9ff', transform: 'translateY(3px)', boxShadow: '0 4px 0 #1C7ED6' }, transition: 'all .2s ease' }}
              >
                Book a Visit
              </Button>
            </Reveal>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 2, md: 3 }, mt: { xs: 9, md: 12 }, mb: 4 }}>
          {STATS.map((s, i) => (
            <Reveal key={s.label} direction="zoom" delay={i * 120}>
              <Box sx={{ textAlign: 'center', p: { xs: 2, md: 3 }, borderRadius: '30px', bgcolor: s.color, color: '#fff', boxShadow: `0 10px 0 ${s.color}99`, height: '100%' }}>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: { xs: 36, md: 52 }, lineHeight: 1 }}>
                  <CountUp end={s.value} suffix={s.suffix} />
                </Typography>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 700, fontSize: { xs: 14, md: 17 }, mt: 1, lineHeight: 1.2 }}>{s.label}</Typography>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Container>
      <Wave color={COLORS.cream} sx={{ mt: 4, mb: '-2px' }} />
    </Section>
  );
}
